import { gql } from "@apollo/client";
import { TROPHY_FIELDS } from "./trophies";

export const CHECK_FEATURE_ACCESS = gql`
  query checkFeatureAccess($feature: String) {
    checkFeatureAccess(feature: $feature)
  }
`;

const PROFILE_CARD_USER_FIELDS = gql`
  fragment ProfileCardUserFragment on User {
    _id
    username
    bio
    avatarUrl
    coverImageUrl
    followerCount
    followingCount
    createdDate
    userGroup {
      type
      active
    }
    rank
    sharedFollows {
      _id
      username
      avatarUrl
      followerCount
      followingCount
      userGroup {
        type
        active
      }
    }
    profile {
      profile {
        countryCode
        socials {
          youtube
          twitch
          tiktok
          twitter
          instagram
        }
      }
    }
    discord {
      username
    }
    riot {
      riotId
      puuid
      active
    }
    steam {
      personaname
      steamid
      active
    }
    epic {
      displayName
      id
    }
    battlenet {
      battletag
    }
    teams {
      nodes {
        id
        name
        slug
        tag
        tagImageUrl
      }
    }
  }
`;

const USER_FIELDS = gql`
  fragment UserFragment on User {
    teams {
      nodes {
        id
        name
        tagImageUrl
        tag
        slug
      }
    }
    montageCount(status: READY)
    followerCount
    followingCount
    followers {
      nodes {
        _id
        username
        avatarUrl
      }
    }
    following {
      nodes {
        _id
        username
        avatarUrl
      }
    }
    _id
    createdDate
    sharedFollows {
      username
      avatarUrl
      userGroup {
        type
      }
    }
    profile {
      profile {
        countryCode
        socials {
          youtube
          twitch
          tiktok
          twitter
          instagram
        }
      }
    }
    pinnedClips {
      createdAt
      id
      isPinned
      internalId
      title
      type
      views
      thumbnailUrl(style: STANDARD)
      url(orientation: LANDSCAPE)
      shareId
      reactions {
        count
        type
      }
      user {
        _id
      }
      moment {
        id
        match {
          outdated
        }
      }
      game
      saved {
        savedBy
        savedAt
      }
      tags {
        key
        value
      }
      developer {
        id
        name
      }
      expires {
        expiresAt
        reason
      }
    }
    discord {
      username
    }
    riot {
      riotId
      puuid
      active
    }
    steam {
      personaname
      steamid
      active
    }
    epic {
      displayName
      id
    }
    battlenet {
      battletag
    }
    userGroup {
      type
      active
    }
    rank
    playerGameIds
    matches {
      thumbnail
      player {
        gameId
      }
      title
      matchPlayedAt
      result
      killCount
      deathCount
      momentCount
      assistCount
      role
      game
      score
      id
      moments(limit: 3, sort: SCORE) {
        id
        title
        timelineLabel
        clip {
          status
          internalId
        }
        player {
          avatar
          displayName
          gameId
          team
        }
        displayTags {
          value
          key
        }
        victimData {
          team
          gameId
          displayName
          avatar
        }
        displayTags {
          value
          key
        }
        icons
        score
        victimCount
      }
    }
  }
`;

export const GET_PROFILE_CARD_DATA = gql`
  ${PROFILE_CARD_USER_FIELDS}

  query getProfileCardData($username: String!) {
    user(username: $username) {
      ...ProfileCardUserFragment
    }
  }
`;

export const GET_USER_PROFILE = gql`
  ${USER_FIELDS}
  ${TROPHY_FIELDS}

  query getUserProfile($query: String!) {
    profile(query: $query) {
      __typename
      username
      avatarUrl
      bio
      coverImageUrl
      clipCount(status: READY)
      followerCount
      followingCount
      playedGames
      trophies {
        ...TrophyFragment
      }
      featuredTrophies {
        ...TrophyFragment
      }

      ... on User {
        ...UserFragment
      }

      ... on UnclaimedProfile {
        proPlayer {
          player {
            countryIso
            name
            fullName
            avatar
          }
          team {
            logo
            name
          }
        }
      }
    }
  }
`;

export const GET_USER_CLIP_COUNT = gql`
  query getUserClipCount($query: String!, $status: ClipStatus) {
    profile(query: $query) {
      clipCount(status: $status)
    }
  }
`;

export const GET_USER_MOBILE_CLIP_COUNT = gql`
  query getUserMobileClipCount($query: String!) {
    profile(query: $query) {
      clipCount(status: READY, orientation: PORTRAIT)
    }
  }
`;

export const GET_USER_CLIPS_FOR_DOWNLOAD = gql`
  query getUserClipsForDownload(
    $query: String!
    $first: Int!
    $after: String
    $orientation: MediaOrientation!
  ) {
    profile(query: $query) {
      clips(
        first: $first
        after: $after
        orientation: $orientation
        statuses: [READY]
      ) {
        pageInfo {
          endCursor
          hasNextPage
          totalCount
        }
        nodes {
          id
          title
          shareId
          url(orientation: $orientation)
        }
      }
    }
  }
`;

export const GET_USER_CLIPS = gql`
  query getUserClips(
    $query: String!
    $first: Int!
    $after: String
    $filters: ClipFilter
    $sort: ClipSort
    $statuses: [ClipStatus!]
    $orientation: MediaOrientation!
    $thumbnailStyle: ThumbnailStyle!
    $saved: Boolean
  ) {
    profile(query: $query) {
      clips(
        sort: $sort
        statuses: $statuses
        first: $first
        after: $after
        filters: $filters
        orientation: $orientation
        saved: $saved
      ) {
        pageInfo {
          endCursor
          hasNextPage
          totalCount
          startCursor
          hasPreviousPage
        }
        nodes {
          createdAt
          game
          id
          tags {
            key
            value
          }
          internalId
          trackId
          isPinned
          previewUrl
          playerGameIdentifier
          thumbnailUrl(style: $thumbnailStyle)
          title
          shareId
          status
          type
          url(orientation: $orientation)
          views
          score
          reactions {
            count
            type
          }
          moment {
            id
            match {
              outdated
            }
          }
          user {
            _id
          }
          saved {
            savedBy
            savedAt
          }
          developer {
            id
            name
          }
          expires {
            expiresAt
            reason
          }
        }
      }
    }
  }
`;

export const GET_USER_MONTAGES = gql`
  query getUserMontages(
    $query: String!
    $first: Int!
    $after: String
    $status: ClipStatus
  ) {
    profile(query: $query) {
      montages(first: $first, after: $after, status: $status) {
        nodes {
          id
          title
          game
          views
          createdAt
          thumbnailUrl(style: OVERLAY)
          url(orientation: LANDSCAPE)
          shareId
          type
          previewUrl
          trackId
          user {
            _id
          }
          internalId
          status
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

export const GET_ME = gql`
  query getMe {
    me {
      newNavOptOutAt
      metadata {
        noEmailSet
        hasSharecodes
      }
      user {
        _id
        admin
        avatar
        canManageBilling
        usingAppStoreInAppPurchases
        usingPlayStoreInAppPurchases

        google {
          active
        }

        userGroup {
          active
          type
          updated
          createdDate
          expires
          paymentDate
          isTrialPeriod
          hasUsedPlatinumTrial
        }

        riot {
          puuid
          riotId
          active
          usedForSignUp
          region
        }
        discord {
          username
          id
          avatar
        }
        faceit {
          nickname
          player_id
          usedForSignUp
        }
        epic {
          usedForSignUp
          displayName
          id
        }
        battlenet {
          usedForSignUp
          battletag
        }
        tiktok {
          hasUploadScope
          display_name
          usedForSignUp
        }
        steam {
          steamid
          personaname
          active
          usedForSignUp
        }
        scope
        rank
        credits {
          providerName
          updated
          createdDate
        }
        discordSignUp
        emailVerified
        createdDate
        username
        usernameLastChanged
        ftue {
          hasCompleted
          isCS
          step
          stepHistory
          page
          skip {
            default
            profile
            games
            platforms
            steam
            sharecode
            socials
            download
            claim
          }
        }
      }
      faceitMembershipTiers
      autoSaveClips
      autoSaveThreshold
      autoclip
      autoclipThreshold
      cs2ClipConfig {
        hud {
          showHud
          color
          topPanel
          radar
          money
          equipment
          chat
          killfeed
          xray
          onlyMyKills
          pinnedKillfeed
          radarSize
          mapZoom
          hudScale
        }
        crosshair {
          mode
          code
        }
      }
      stats {
        totalClips
        totalViews
      }
      # Aliased so the app keeps reading clipQuota; the data now comes from quentin.
      clipQuota: clipQuotaNew {
        limit
        used
        nextAvailableAt
      }
      profile {
        id
        ccpaPrivacyPreference
        profile {
          countryCode
          locale
          socials {
            twitch
            tiktok
            steam
            instagram
            twitter
            youtube
          }
          youtubeChannels
        }

        username
        createdDate
        appVersion
        gamersClub {
          enabled
          steamId64
        }
        settings {
          usernameChange
        }
        following
      }
    }
  }
`;

export const VIEWER_AUTH_CODE = gql`
  query ViewerAuthCode {
    me {
      authenticationCode
    }
  }
`;

export const LOGIN = gql`
  query LoginQuery($email: String!, $password: String!) {
    authentication(email: $email, password: $password)
  }
`;

export const SIGNUP = gql`
  mutation Mutation(
    $email: String!
    $password: String!
    $ref: ID
    $lastViewedShareId: String
  ) {
    signup(
      email: $email
      password: $password
      ref: $ref
      lastViewedShareId: $lastViewedShareId
    ) {
      errors {
        code
        field
        message
      }
      success
      token
    }
  }
`;

export const USER_MOBILE_AUTH_TOKEN_EXCHANGE = gql`
  mutation UserMobileAuthTokenExchange(
    $input: UserMobileAuthTokenExchangeInput!
  ) {
    userMobileAuthTokenExchange(input: $input) {
      authToken
    }
  }
`;

export const USER_UPDATE = gql`
  mutation UserUpdate($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      errors {
        code
        field
        message
      }
    }
  }
`;

export const USER_UPDATE_AUTO_SAVE = gql`
  mutation UserUpdateAutoSave($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      errors {
        code
        field
        message
      }
      viewer {
        autoSaveClips
        autoSaveThreshold
      }
    }
  }
`;

export const USER_UPDATE_AUTOCLIP = gql`
  mutation UserUpdateAutoclip($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      errors {
        code
        field
        message
      }
      viewer {
        autoclip
        autoclipThreshold
      }
    }
  }
`;

export const USER_UPDATE_CS2_CLIP_CONFIG = gql`
  mutation UserUpdateCs2ClipConfig($input: UserUpdateCs2ClipConfigInput!) {
    userUpdateCs2ClipConfig(input: $input) {
      errors {
        code
        field
        message
      }
      viewer {
        cs2ClipConfig {
          hud {
            showHud
            color
            topPanel
            radar
            money
            equipment
            chat
            killfeed
            xray
            onlyMyKills
            pinnedKillfeed
            radarSize
            mapZoom
            hudScale
          }
          crosshair {
            mode
            code
          }
        }
      }
    }
  }
`;

export const CS2_CONFIG_UPLOAD_URL = gql`
  query Cs2ConfigUploadUrl {
    me {
      mediaUploadUrl(type: TXT, purpose: CS2_CONFIG) {
        url
        key
        fields {
          name
          value
        }
      }
    }
  }
`;
